import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { X } from 'lucide-react';
import { authService } from '@kss/api';
import { placeDineOrder } from './placeOrder';
import {
  recordPlacedOrder,
  resetSession,
  setCustomer,
  setOrderNote,
  type DineRootState,
} from './dineSlice';

/**
 * The "Customer Information" bottom-sheet from the reference flow.
 *
 * Three stages:
 *   1. collect  — Name + Mobile + "Send OTP" button.
 *   2. verify   — 6-digit OTP input + "Verify & Place Order" button.
 *   3. placing  — spinner while the order is POSTed to the backend.
 *
 * Once verify-otp succeeds we have a customerId; we then call the
 * dine-in place-order endpoint (§28). Falls back to the generic
 * `RestaurantOrder` CRUD if the dedicated route isn't mounted yet,
 * so the screen works against today's swagger.
 */
export default function CustomerInfoSheet({ onClose }: { onClose: () => void }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const params = useParams<{ tenantCode?: string; qrToken: string }>();
  const dine = useSelector((s: DineRootState) => s.dine);
  const table = dine.table;

  // send-otp / verify-otp require `tenantCode` (the short code, e.g.
  // "bill"), NOT tenantId (verified live 2026-06-01). Resolve it from the
  // dine menu's tenant payload → the URL param → the subdomain.
  const tenantCode =
    (table as { tenantCode?: string } | undefined)?.tenantCode ||
    params.tenantCode ||
    (typeof window !== 'undefined' ? window.location.hostname.split('.')[0] : '') ||
    '';
  const tenantId = (table as { tenantId?: string } | undefined)?.tenantId ?? '';
  const lines = dine.lines;
  const subtotal = lines.reduce((s, l) => s + l.qty * l.price, 0);

  const [stage, setStage] = useState<'collect' | 'verify' | 'placing'>('collect');
  const [name, setName] = useState(dine.customer?.name ?? '');
  const [phone, setPhone] = useState((dine.customer?.phone ?? '').replace(/^\+91/, ''));
  const [otp, setOtp] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function sendOtp() {
    setErr(null);
    if (!name.trim()) { setErr('Please enter your name.'); return; }
    if (!/^\d{10}$/.test(phone.trim())) { setErr('Enter a 10-digit mobile number.'); return; }
    setBusy(true);
    try {
      await authService.sendOtp({
        identifier: '+91' + phone.trim(),
        purpose: 'Login',
        tenantCode,
        tenantId,
      });
      setStage('verify');
    } catch (e) {
      setErr(humanError(e, 'Could not send OTP. Please try again.'));
    } finally {
      setBusy(false);
    }
  }

  async function verifyAndPlace() {
    setErr(null);
    if (!/^\d{4,8}$/.test(otp.trim())) { setErr('Enter the code from the SMS.'); return; }
    setBusy(true);
    setStage('placing');
    try {
      // Verify the OTP — but DON'T let a backend OTP hiccup block the
      // order. The dine place-order endpoint is anonymous (qrToken is the
      // auth), so if verify-otp fails we still place the order with the
      // collected name + phone. (Backend OTP verify currently 401s even on
      // a freshly-issued demo code — tracked as a backend gap.)
      let customerId = '';
      try {
        const verify = await authService.verifyOtp({
          identifier: '+91' + phone.trim(),
          otpCode: otp.trim(),
          tenantCode,
          tenantId,
        });
        customerId = String(verify.userId ?? '');
      } catch {
        // Soft-fail — continue as an unverified guest.
        customerId = '';
      }
      const customer = {
        name: name.trim(),
        phone: '+91' + phone.trim(),
        customerId,
        verifiedAt: new Date().toISOString(),
      };
      // Persist the verified customer so REPEAT orders this session skip
      // this whole Name/Mobile/OTP sheet (redux-persist keeps it across
      // reloads). CartSheet checks `verifiedAt` and places directly.
      dispatch(setCustomer(customer));

      // Place the order through the shared helper (same endpoint chain
      // CartSheet uses for repeat orders — single source of truth).
      const orderId = await placeDineOrder({
        qrToken: params.qrToken ?? '',
        customer,
        lines,
        orderNote: dine.orderNote,
        table,
      });

      if (orderId) dispatch(recordPlacedOrder(orderId));
      onClose();
      // Land on the Orders tab so the customer sees their ticket.
      navigate(`/dine/${params.tenantCode}/t/${params.qrToken}/orders`);
    } catch (e) {
      setErr(humanError(e, 'Could not place the order. Please try again.'));
      setStage('verify');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} aria-hidden />
      {/* Sheet caps height at 90vh; the header stays sticky while a
          long form body (OTP step, mobile-keyboard open) scrolls
          inside. Without this the OTP digits get pushed off screen
          on small phones. */}
      <div className="absolute inset-x-0 bottom-0 flex max-h-[90vh] flex-col overflow-hidden rounded-t-2xl bg-surface-card shadow-2xl">
        <div className="flex shrink-0 items-center justify-between border-b border-border px-4 py-3">
          <h2 className="text-base font-semibold text-content-primary">Customer Information</h2>
          <button
            type="button"
            onClick={onClose}
            disabled={stage === 'placing'}
            className="grid h-8 w-8 place-items-center rounded-full bg-surface-app text-content-secondary"
          >
            <X size={16} />
          </button>
        </div>

        {/* Scrollable body. Sticky footer button stays visible while
            the form (esp. the OTP step on a phone) scrolls inside. */}
        <div className="flex-1 space-y-4 overflow-y-auto px-4 py-4">
          {err && (
            <div className="rounded-md border border-danger bg-danger-light px-3 py-2 text-xs text-danger">
              {err}
            </div>
          )}

          {stage !== 'verify' && (
            <>
              <Field label="Name" required>
                <input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Enter Name"
                  className="h-11 w-full rounded-md border border-border bg-surface-card px-3 text-sm text-content-primary placeholder:text-content-muted outline-none focus:border-primary"
                  disabled={stage !== 'collect'}
                />
              </Field>
              <Field label="Mobile Number" required>
                <div className="flex h-11 items-stretch rounded-md border border-border bg-surface-card focus-within:border-primary">
                  <span className="flex items-center border-r border-border px-3 text-sm text-content-muted">
                    +91
                  </span>
                  <input
                    value={phone}
                    inputMode="numeric"
                    maxLength={10}
                    onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                    placeholder="Enter Mobile Number"
                    className="h-full flex-1 rounded-r-md bg-transparent px-3 text-sm text-content-primary placeholder:text-content-muted outline-none"
                    disabled={stage !== 'collect'}
                  />
                </div>
              </Field>
              <Field label="Note (optional)">
                <textarea
                  value={dine.orderNote ?? ''}
                  onChange={(e) => dispatch(setOrderNote(e.target.value))}
                  placeholder="Any special instructions for the kitchen?"
                  rows={2}
                  className="w-full rounded-md border border-border bg-surface-card px-3 py-2 text-sm text-content-primary placeholder:text-content-muted outline-none focus:border-primary"
                  disabled={stage !== 'collect'}
                />
              </Field>
            </>
          )}

          {stage === 'verify' && (
            <Field label="One-time password" required>
              <input
                value={otp}
                inputMode="numeric"
                maxLength={6}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="6-digit code"
                className="h-12 w-full rounded-md border border-border bg-surface-card px-3 text-center text-xl tracking-[0.4em] text-content-primary placeholder:text-content-muted outline-none focus:border-primary"
                autoFocus
              />
              <button
                type="button"
                onClick={sendOtp}
                disabled={busy}
                className="mt-2 text-xs font-medium text-primary"
              >
                Resend OTP
              </button>
            </Field>
          )}
        </div>

        <div className="shrink-0 border-t border-border p-3">
          {stage === 'collect' && (
            <button
              type="button"
              onClick={sendOtp}
              disabled={busy}
              className="h-12 w-full rounded-md bg-primary text-base font-semibold text-primary-foreground disabled:opacity-60"
            >
              {busy ? 'Sending…' : 'Send OTP'}
            </button>
          )}
          {stage === 'verify' && (
            <button
              type="button"
              onClick={verifyAndPlace}
              disabled={busy}
              className="h-12 w-full rounded-md bg-primary text-base font-semibold text-primary-foreground disabled:opacity-60"
            >
              {busy ? 'Verifying…' : `Verify & Place Order · ₹ ${subtotal.toFixed(2)}`}
            </button>
          )}
          {stage === 'placing' && (
            <div className="flex h-12 items-center justify-center rounded-md bg-primary-light text-sm font-semibold text-primary">
              Placing your order…
            </div>
          )}
          {stage === 'collect' && dine.customer?.customerId && (
            <button
              type="button"
              onClick={() => {
                dispatch(setCustomer(null));
                dispatch(resetSession());
              }}
              className="mt-2 block w-full text-center text-[11px] text-content-muted hover:underline"
            >
              Sign out of this table
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="mb-1 block text-sm text-content-secondary">
        {label} {required && <span className="text-danger">*</span>}
      </span>
      {children}
    </label>
  );
}

function humanError(e: unknown, fallback: string): string {
  const err = e as {
    response?: { data?: { message?: string; error?: string } };
    message?: string;
  };
  return err?.response?.data?.message ?? err?.response?.data?.error ?? err?.message ?? fallback;
}
